import { CloseOutlined } from '@ant-design/icons';
import { Button, Modal } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Close, LinkOutlined } from '@mui/icons-material';
import { Row } from 'antd';
import { get, find } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAllBook } from 'store/books/selectors';
import { useBookUnits } from 'store/booksUnits/selectors';
import styled from 'styled-components';
import { BACK_TO_BOOK } from 'utils/config/reference';
import COLOR from 'utils/constants/color';
import { ebookConfigDinamic, generateUrlForNonEbook } from 'utils/lib/ebookHelper';
import { disableBackWrad, disableForWard } from '../helper';
import { useCurrentUser } from 'store/users/selectors';
import AssignmentsComponent from '../../../assignments';
import useImageCache from 'components/hooks/useImageCache/useImageCache';
import BookImage from 'components/hooks/useImageCache/bookImage';

const ApPreview = ({
  open,
  handleClose,
  selectedItems,
  CHAPTER_ID = '69',
  styles,
  extensionActivity,
}) => {
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const backBtnRef = useRef();
  const forwardBtnRef = useRef();
  const [page, setPage] = useState('');
  const [preloadedAssets, setPreloadedAssets] = useState({});

  // for fetch Book Content
  const booksAllList = useAllBook();
  const bookUnitList = useBookUnits();
  const UNIT_PAGES = get(find(bookUnitList, ['bookId', CHAPTER_ID]), 'pages', []);

  const getSelectedItem = useMemo(() => {
    if (UNIT_PAGES) {
      const filteredData = UNIT_PAGES?.find((item) => item?.id === selectedItems?.id);
      return filteredData;
    }
    return {};
  }, [selectedItems, UNIT_PAGES]);

  useEffect(() => {
    if (extensionActivity?.linkFromEbook) {
      setPage(extensionActivity?.linkFromEbook);
    } else if (getSelectedItem?.start && getSelectedItem?.start !== page) {
      setPage(getSelectedItem.start);
    }
  }, [getSelectedItem?.start]);

  const data = ebookConfigDinamic(CHAPTER_ID, booksAllList);
  const content = useMemo(() => data?.content || [], [data]);

  // Convert content to assets format for the hook
  const assets = useMemo(() => {
    return content.map((item) => ({
      imageUrl: item.src,
      id: item.pageId,
    }));
  }, [content]);
  useImageCache(assets, setPreloadedAssets, 5);

  const getSrcPage = useMemo(() => {
    let dataPage = find(content, (x) => x.pageId == Number(page));
    if (dataPage) {
      return dataPage.src;
    }
  }, [content, page]);

  const currentPageIndex = useMemo(() => {
    return content.findIndex((item) => item.pageId == Number(page));
  }, [content, page]);

  const redirectPage = () => {
    handleClose();
    const backToLabel = BACK_TO_BOOK[CHAPTER_ID];
    const url = generateUrlForNonEbook(
      getSelectedItem?.id,
      page,
      null,
      null,
      backToLabel,
      null,
      CHAPTER_ID,
    );
    navigate(url);
  };

  const handlePageChange = (direction) => {
    if (direction === 'next') {
      setPage(parseInt(page, 10) + 1);
    } else {
      setPage(parseInt(page, 10) - 1);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', checkKey);
    return () => {
      document.removeEventListener('keydown', () => {});
    };
  }, []);

  function checkKey(e) {
    if (e) {
      e = e || window.event;
      if (e.keyCode == '37') {
        if (backBtnRef.current && !backBtnRef.current.disabled) {
          backBtnRef.current.click();
        }
      } else if (e.keyCode == '39') {
        if (forwardBtnRef.current && !forwardBtnRef.current.disabled) {
          forwardBtnRef.current.click();
        }
      }
    }
  }

  return (
    <>
      {currentUser?.role === 'teacher' && open && (
        <StyledModal
          open={open}
          onClose={handleClose}
          className={'lessonPlanPopUp main-book-popup'}
        >
          <LearnPlanWrapper>
            <LessionPlanContainer isStudent={currentUser?.role === 'student'}>
              <div className="w-full align-center flex justify-center">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LinkOutlined />}
                  onClick={redirectPage}
                  style={{ marginBottom: '10px' }}
                >
                  Go to Page
                </Button>
              </div>

              <CloseBtn
                onClick={() => {
                  handleClose(false);
                }}
              >
                <StyledCloseIcon />
              </CloseBtn>
              <div className="balconPreviewPrintWrapper">
                <PrintWrapper>
                  <ButtonContainer
                    ref={backBtnRef}
                    disabled={disableBackWrad(page, getSelectedItem?.start)}
                    onClick={() => handlePageChange('back')}
                    left={0}
                    className="cstmLeftArow"
                  >
                    <StyledArrowBackIos />
                  </ButtonContainer>
                  <BookImage
                    src={getSrcPage}
                    imageCache={preloadedAssets?.images || {}}
                    alt={`Book page ${page}`}
                  />
                  <ButtonContainer
                    ref={forwardBtnRef}
                    disabled={disableForWard(page, getSelectedItem?.finish)}
                    onClick={() => handlePageChange('next')}
                    right={0}
                    className="cstmRghtArow"
                  >
                    <StyledArrowForwardIos />
                  </ButtonContainer>
                </PrintWrapper>
                <div className="balconPreviewPrintRight">
                <AssignmentsComponent
                  bookId={CHAPTER_ID}
                  pageId={page}
                  extensionActivityId={extensionActivity?.id}
                  from={'extensionActivity'}
                  renderAssignments={'admin'}
                />
                </div>
              </div>
            </LessionPlanContainer>
          </LearnPlanWrapper>
        </StyledModal>
      )}

      {currentUser?.role === 'student' && open && (
        <Wrapper
          width={styles.width}
          height={styles.height + 36}
          className={open ? 'main-book-popup' : ''}
        >
          <CloseButton onClick={handleClose} />
          <HeaderRow
            style={{
              justifyContent: extensionActivity?.disableNavigation ? 'center' : 'space-between',
            }}
          >
            {extensionActivity?.disableNavigation ? null : (
              <ButtonContainer
                ref={backBtnRef}
                disabled={disableBackWrad(page, getSelectedItem?.start)}
                onClick={() => handlePageChange('back')}
                left={0}
                className="cstmLeftArow"
              >
                <StyledArrowBackIos />
              </ButtonContainer>
            )}

            <StyledGoToPageButton
              variant="contained"
              color="primary"
              startIcon={<LinkOutlined />}
              onClick={redirectPage}
            >
              Go to Page
            </StyledGoToPageButton>
            {extensionActivity?.disableNavigation ? null : (
              <ButtonContainer
                ref={forwardBtnRef}
                disabled={disableForWard(page, getSelectedItem?.finish)}
                onClick={() => handlePageChange('next')}
                right={0}
                className="cstmRghtArow"
              >
                <StyledArrowForwardIos />
              </ButtonContainer>
            )}
          </HeaderRow>
          <BookImage
            src={getSrcPage}
            imageCache={preloadedAssets?.images || {}}
            alt={`Book page ${page}`}
          />
        </Wrapper>
      )}
    </>
  );
};

export default React.memo(ApPreview);

const PrintWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
`;

const StyledCloseIcon = styled(Close)`
  color: #fff;
  font-weight: bolder;
  font-size: 25px;
`;

const CloseBtn = styled.div`
  background-color: rgb(204, 80, 71);
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  position: absolute;
  top: 10px;
  right: 10px;
  color: white;
  z-index: 1;
`;

const StyledModal = styled(Modal)`
  height: 100%;
  width: 100%;
  background-color: transparent;
  z-index: 1000 !important;
`;

const LearnPlanWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: transparent;
  overflow: hidden;
`;

const LessionPlanContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  // height: 100%;
  max-width: 90%;
  max-height: 90%;
  position: relative;
  padding: 20px;
  background-color: white;
  justify-content: ${(props) => (props.isStudent ? 'center' : 'flex-start')};
  align-items: start;
  @media (max-width: 1024px){
    min-height: 600px;
  }
  @media (max-width: 990px){
    min-height: 550px;
  }
  @media (max-width: 800px){
    min-height: 450px;
  }
  @media (max-width: 700px){
    min-height: 400px;
  }
  @media (max-width: 600px){
    min-height: 350px;
  }
  @media (max-width: 450px){
    min-height: 480px;
  }
`;

const LoadingIndicator = styled.div`
  position: absolute;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 10;
`;

// Styled Components
const CloseButton = styled(CloseOutlined)`
  position: absolute;
  top: -10px;
  right: -10px;
  cursor: pointer;
  font-size: 16px;
  color: white;
  background: red;
  padding: 5px;
  border-radius: 50%;
`;

const HeaderRow = styled(Row)`
  height: 47px;
  padding: 5px;
  justify-content: space-between;
  display: flex;
  background-color: ${COLOR.white};
  padding-right: 20px;
`;

const StyledGoToPageButton = styled(Button)`
  margin-bottom: 10px;
`;

const StyledArrowBackIos = styled(ArrowBackIos)`
  font-size: 20px;
`;

const StyledArrowForwardIos = styled(ArrowForwardIos)`
  font-size: 20px;
`;

const Wrapper = styled.div`
  position: absolute;
  top: 0px;
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 2;
  box-shadow:
    7px -3px 26px 20px rgb(66 66 66),
    -4px -3px 26px 15px rgb(95 109 110);
`;

const ButtonContainer = styled.button`
  background-color: transparent;
  border: none;
  color: ${COLOR.black600};
  cursor: pointer;
  outline: none;
  transition: all 300ms ease;
  width: 40px;
  z-index: 1;
`;
